import React, { useEffect, useState, useCallback } from "react";
import { Box, Grid, Typography, IconButton } from "@mui/material";
import { ReactComponent as RedirectIcon } from "../../assets/PepWise2.0/Redirect.svg";

import { ReactComponent as DataObservabilityLandingIcon } from '../../assets/PepWise2.0/Data Observability Landing.svg';
import { ReactComponent as DataEstateLandingIcon } from '../../assets/PepWise2.0/D&AI Executive Landing.svg';
import { ReactComponent as DataXopsLandingIcon } from '../../assets/PepWise2.0/D&AI Xops Landing.svg';
import { ReactComponent as ProgramObsLandingIcon } from '../../assets/PepWise2.0/Program Landing.svg';
import { ReactComponent as RMLandingIcon } from '../../assets/PepWise2.0/FinOps Dashboards Landing.svg';
import { ReactComponent as PlatformLandingIcon } from '../../assets/PepWise2.0/Platform Landing.svg';
import { ReactComponent as MLLandingIcon } from '../../assets/PepWise2.0/Machine Learning Landing.svg';
import { ReactComponent as KPIandAlertsLandingIcon } from '../../assets/PepWise2.0/Data KPIs & Alerts Landing.svg';
import { ReactComponent as ProductCatalogLandingIcon } from '../../assets/PepWise2.0/Product Catalog.svg';
import { ReactComponent as ProductSuccessLandingIcon } from '../../assets/PepWise2.0/Product_Success.svg';
import { ReactComponent as AlertActiveIcon } from '../../assets/PepWise2.0/Alerts_Selected_White.svg';
import { ReactComponent as IncidentsWhiteIcon } from '../../assets/PepWise2.0/Incidents_White.svg';
import { useTheme } from "@mui/material/styles";
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { checkRole } from 'utils/roleUtils';
import { FINOPS_ALL_ROLES } from 'components/FinOpsExecutive/finopsRoles';
import { RESTRICTED_ROLE_NAMES, RESTRICTED_ROUTES_FOR_PwC } from "utils/common-util";

type RecentItem = {
  id: string;
  title: string;
  route: string;
  kind: "parent" | "child";
  tagline?: string;
  iconKey?: string;
};

const KEY = "recentVisitedDashboards";

const readRecent = (): RecentItem[] => {
  try {
    const raw = localStorage.getItem(KEY);
    const data: RecentItem[] = raw ? JSON.parse(raw) : [];
    // safety: only keep 2 if your writer ever exceeded it
    if (!Array.isArray(data)) return [];

    return data
      .filter(
        (item) =>
          !item.route?.startsWith("/credsManagement")
      )
      .slice(0, 4);
  } catch {
    return [];
  }
};

const RecentVisited: React.FC = () => {
  const theme = useTheme();
  const colors = theme.palette;
  const [recent, setRecent] = useState<RecentItem[]>([]);
  const [{ role }] = usePepwiseAuthController();
  const canAccessFinOpsExec = checkRole(role, FINOPS_ALL_ROLES);

  const isRestricted = checkRole(role, RESTRICTED_ROLE_NAMES);

  const refresh = useCallback(() => {
    const all = readRecent();
    const filtered = isRestricted
      ? all.filter(
          (item) =>
            !RESTRICTED_ROUTES_FOR_PwC.some(
              (p) => item.route === p || item.route?.startsWith(p + '/')
            )
        )
      : all;
    setRecent(filtered);
  }, [isRestricted]);

  const iconRegistry: Record<string, JSX.Element> = {
    // Top-level menuItems
    "D&AIExecutive": <DataEstateLandingIcon width={60} height={60} />,
    "D&AIMonthlyXOps": <DataXopsLandingIcon width={60} height={60} />,
    "DataPipeline": <DataObservabilityLandingIcon width={60} height={60} />,
    "ProgramExecutive": <ProgramObsLandingIcon width={60} height={60} />,
    "finops": <RMLandingIcon width={60} height={60} />,
    "PlatformObservability": <PlatformLandingIcon width={60} height={60} />,
    "mlObservability": <MLLandingIcon width={60} height={60} />,
    "DataKPI-and-Alerts": <KPIandAlertsLandingIcon width={60} height={60} />,
    "ProductCatalog": <ProductCatalogLandingIcon width={60} height={60} />,
    "ProductSuccess": <ProductSuccessLandingIcon width={60} height={60} />,

    // Children under "D&AI Executive"
    "de-executive-summary": <DataEstateLandingIcon width={60} height={60} />,
    "de-storage-summary": <DataEstateLandingIcon width={60} height={60} />,
    "de-queries-summary": <DataEstateLandingIcon width={60} height={60} />,
    "de-data-processing": <DataEstateLandingIcon width={60} height={60} />,
    "de-bi-summary": <DataEstateLandingIcon width={60} height={60} />,
    "de-cost-summary": <DataEstateLandingIcon width={60} height={60} />,
    "de-major-incident": <DataEstateLandingIcon width={60} height={60} />,

    // Children under "Data Observability"
    "summary": <DataXopsLandingIcon width={60} height={60} />,


    // Children under "Data Observability"
    "dp-sustain": <DataObservabilityLandingIcon width={60} height={60} />,
    "dp-program": <DataObservabilityLandingIcon width={60} height={60} />,
    "dp-end-to-end": <DataObservabilityLandingIcon width={60} height={60} />,
    "end-to-end-sector": <DataObservabilityLandingIcon width={60} height={60} />,
    "end-to-end-program": <DataObservabilityLandingIcon width={60} height={60} />,
    "PlatformHealth": <DataObservabilityLandingIcon width={60} height={60} />,
    "PipelineRunStatus": <DataObservabilityLandingIcon width={60} height={60} />,
    "CrossFlowNavigator": <DataObservabilityLandingIcon width={60} height={60} />,
    "end-to-end-data-recon": <DataObservabilityLandingIcon width={60} height={60} />,


    // Children under "Program Executive"
    "ibp": <ProgramObsLandingIcon width={60} height={60} />,
    "mdip": <ProgramObsLandingIcon width={60} height={60} />,

    // Children under "FinOps Dashboards"
    "rm": <RMLandingIcon width={60} height={60} />,
    "platform-cost": <RMLandingIcon width={60} height={60} />,

    // platformChildren
    "Teradata-Jobs": <PlatformLandingIcon width={60} height={60} />,
    "Tableau": <PlatformLandingIcon width={60} height={60} />,
    "Data-Growth": <PlatformLandingIcon width={60} height={60} />,
    "Databricks": <PlatformLandingIcon width={60} height={60} />,
    "Presto": <PlatformLandingIcon width={60} height={60} />,
    "PowerBI": <PlatformLandingIcon width={60} height={60} />,
    "Data-Management-Framework": <PlatformLandingIcon width={60} height={60} />,
    "Enterprise-Platform-Modernization-Dashboard": <PlatformLandingIcon width={60} height={60} />,

    //KPI and Alerts children
    "KPIStore": <KPIandAlertsLandingIcon width={60} height={60} />,
    "Alert-Framework": <KPIandAlertsLandingIcon width={60} height={60} />,

    "Incidents": <IncidentsWhiteIcon width={40} height={40} />,
    "Alerts" : <AlertActiveIcon width={40} height={40} />,
  }

  useEffect(() => {
    refresh();
    // update if other tabs/windows modify localStorage
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  const isFinOpsExecEntry = (d: RecentItem) =>
    d.iconKey === 'finOpsIcon' || d.id === 'finOps';

  const visibleRecent = recent.filter((d) => {
    if (isFinOpsExecEntry(d)) return canAccessFinOpsExec;
    return true;
  });

  if (!visibleRecent.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {visibleRecent.map((d) => (
          <Grid key={`${d.kind}:${d.id}`} item xs={12} sm={6} md={4} lg={3}>
            <Box
              sx={{
                bgcolor: `${colors.landingPageTile.background} !important`,
                color: `${colors.landingPageTile.text} !important`,
                px: 2,
                py: 1.5,
                minHeight: 62,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.25,
                cursor: "pointer",
                position: 'relative',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '4px 8px 54px 0px #0000006B',
                  height: '50px',
                  zIndex: 10,
                }
              }}
              onClick={() => (window.location.href = d.route)}
              onContextMenu={(e) => {
                e.preventDefault();
                window.open(d.route, "_blank");
              }}
            >

              <Box aria-hidden sx={{ display: "grid", placeItems: "center" }}>
                {d.iconKey ? iconRegistry[d.iconKey] : null}
              </Box>

              <Box sx={{ flex: 1, ml: 2, minWidth: 0 }}>
                {/* <Typography
                  variant="dashboardTileTitle"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                  }}
                  title={d.title}
                >
                  {d.title}
                </Typography> */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Typography
                    variant="dashboardTileTitle"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={d.title}
                  >
                    {d.title}
                  </Typography>
                </Box>
                {d.tagline ? (
                  <Typography
                    variant="dashboardTileCaption"
                    sx={{
                      color: `${colors.landingPageTile.parentTagline} !important`,
                    }}
                    title={d.tagline}
                  >
                    {d.tagline}
                  </Typography>
                ) : null}
              </Box>

              <IconButton
                size="small"
                aria-label="Open"
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = d.route;
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(d.route, "_blank");
                }}
              >
                <RedirectIcon width="24px" height="24px" />
              </IconButton>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default RecentVisited;